const API_URL = import.meta.env.VITE_API_URL || '';

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

class ApiClient {
  private baseUrl: string;
  private getToken: () => string | null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.getToken = () => {
      const stored = localStorage.getItem('auth-storage');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.state?.token || null;
      }
      return null;
    };
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { params, ...fetchOptions } = options;
    
    let url = `${this.baseUrl}${endpoint}`;
    
    // Add query params
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    // Add auth token if available
    const token = this.getToken();
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'An error occurred' }));
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // GET request
  async get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  // POST request
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // PATCH request
  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export const api = new ApiClient(API_URL);

// Typed API methods
export const placesApi = {
  getAll: (params?: { category?: string; country?: string; city?: string; lat?: number; lng?: number; radius?: number }) =>
    api.get<{ places: Place[]; total: number }>('/api/places', params),
  getById: (id: string) => api.get<Place>(`/api/places/${id}`),
  getNearby: (lat: number, lng: number, radius?: number) =>
    api.get<Place[]>('/api/places/nearby', { lat, lng, radius }),
  getReviews: (id: string) => api.get<Review[]>(`/api/places/${id}/reviews`),
  createReview: (id: string, data: { rating: number; content: string }) =>
    api.post<Review>(`/api/places/${id}/reviews`, data),
};

export const staysApi = {
  getAll: (params?: { country?: string; city?: string; minPrice?: number; maxPrice?: number; koreanHost?: boolean }) =>
    api.get<{ stays: Stay[]; total: number }>('/api/stays', params),
  getById: (id: string) => api.get<Stay>(`/api/stays/${id}`),
  getFeatured: () => api.get<Stay[]>('/api/stays/featured'),
  trackClick: (id: string) => api.post(`/api/stays/${id}/click`),
};

export const postsApi = {
  getAll: (params?: { category?: string; country?: string; page?: number; limit?: number }) =>
    api.get<{ posts: Post[]; total: number }>('/api/posts', params),
  getById: (id: string) => api.get<Post>(`/api/posts/${id}`),
  getHot: () => api.get<Post[]>('/api/posts/hot'),
  create: (data: { title: string; content: string; category: string }) =>
    api.post<Post>('/api/posts', data),
  update: (id: string, data: { title?: string; content?: string }) =>
    api.put<Post>(`/api/posts/${id}`, data),
  delete: (id: string) => api.delete(`/api/posts/${id}`),
  like: (id: string) => api.post(`/api/posts/${id}/like`),
  getComments: (id: string) => api.get<Comment[]>(`/api/posts/${id}/comments`),
  createComment: (id: string, data: { content: string; parentId?: string }) =>
    api.post<Comment>(`/api/posts/${id}/comments`, data),
};

export const jobsApi = {
  getAll: (params?: { query?: string; location?: string; category?: string; jobType?: string; page?: number }) =>
    api.get<{ jobs: Job[]; total: number }>('/api/jobs', params),
  getById: (id: string) => api.get<Job>(`/api/jobs/${id}`),
  createAlert: (data: { email: string; query?: string; location?: string; category?: string }) =>
    api.post('/api/jobs/alerts', data),
};

export interface UserStats {
  postsCount: number;
  commentsCount: number;
  reviewsCount: number;
  likesReceived: number;
  memberSince: string;
}

export const authApi = {
  login: (data: { email: string; password: string }) =>
    api.post<{ user: User; token: string }>('/api/auth/login', data),
  register: (data: { name: string; email: string; password: string }) =>
    api.post<{ user: User; token: string; message?: string }>('/api/auth/register', data),
  logout: () => api.post('/api/auth/logout'),
  me: () => api.get<{ user: User }>('/api/auth/me'),
  updateProfile: (data: Partial<User>) => api.put<{ success: boolean; user: User }>('/api/auth/me', data),
  getStats: () => api.get<UserStats>('/api/auth/stats'),
  verifyEmail: (token: string) =>
    api.get<{ success: boolean; message: string }>('/api/auth/verify-email', { token }),
  resendVerification: (email: string) =>
    api.post<{ success: boolean; message: string }>('/api/auth/resend-verification', { email }),
  forgotPassword: (email: string) =>
    api.post<{ success: boolean; message: string }>('/api/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post<{ success: boolean; message: string }>('/api/auth/reset-password', { token, password }),
};

export const eventsApi = {
  getAll: (params?: { category?: string; country?: string }) =>
    api.get<{ events: Event[]; total: number }>('/api/events', params),
  getById: (id: string) => api.get<Event>(`/api/events/${id}`),
};

export const countriesApi = {
  getAll: () => api.get<CountryGuide[]>('/api/countries'),
  getByCode: (code: string) => api.get<CountryGuide>(`/api/countries/${code}`),
};

// Type definitions
export interface Place {
  id: string;
  googlePlaceId?: string;
  name: string;
  nameKo?: string;
  category: string;
  subcategory?: string;
  description?: string;
  address: string;
  city: string;
  country: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  phone?: string;
  website?: string;
  googleRating?: number;
  googleReviewsCount?: number;
  ourRating?: number;
  ourReviewsCount: number;
  hours?: Record<string, string>;
  photos?: string[];
  isKoreanOwned: boolean;
  isVerified: boolean;
  youtubeVideoId?: string;
  createdAt: string;
}

export interface Review {
  id: string;
  placeId: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  rating: number;
  content: string;
  photos?: string[];
  helpfulCount: number;
  createdAt: string;
}

export interface Stay {
  id: string;
  title: string;
  titleKo?: string;
  description?: string;
  hostName: string;
  hostIsKorean: boolean;
  hostAvatarUrl?: string;
  city: string;
  country: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  pricePerNight: number;
  currency: string;
  rating?: number;
  reviewsCount?: number;
  photos?: string[];
  amenities?: string[];
  airbnbUrl?: string;
  bookingUrl?: string;
  isLongTermOk: boolean;
  isFeatured: boolean;
  createdAt: string;
}

export interface Post {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  title: string;
  content: string;
  category: string;
  tags?: string[];
  countryCode?: string;
  viewsCount: number;
  likesCount: number;
  commentsCount: number;
  isPinned: boolean;
  isFeatured: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Comment {
  id: string;
  postId: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  parentId?: string;
  content: string;
  likesCount: number;
  createdAt: string;
}

export interface Job {
  id: string;
  externalId?: string;
  title: string;
  company: string;
  companyLogoUrl?: string;
  location: string;
  countryCode?: string;
  isRemote: boolean;
  jobType: string;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency: string;
  description: string;
  requirements?: string[];
  skills?: string[];
  applyUrl: string;
  isKoreanRequired: boolean;
  isKoreanCompany: boolean;
  visaSponsorship?: string;
  isFeatured: boolean;
  source?: string;
  createdAt: string;
}

export interface Deal {
  id: string;
  title: string;
  titleKo?: string;
  description?: string;
  category: string;
  provider: string;
  originalPrice?: number;
  dealPrice?: number;
  currency: string;
  discountPercent?: number;
  dealUrl: string;
  imageUrl?: string;
  validFrom?: string;
  validUntil?: string;
  isFeatured: boolean;
  viewsCount: number;
  clicksCount: number;
  createdAt: string;
}

export interface Event {
  id: string;
  title: string;
  titleKo?: string;
  description?: string;
  category: string;
  organizer: string;
  venue: string;
  city: string;
  countryCode: string;
  address: string;
  latitude?: number;
  longitude?: number;
  startDate: string;
  endDate?: string;
  isOnline: boolean;
  onlineUrl?: string;
  ticketUrl?: string;
  imageUrl?: string;
  price?: string;
  isFeatured: boolean;
  createdAt: string;
}

export interface CountryGuide {
  id: string;
  countryCode: string;
  countryName: string;
  countryNameKo?: string;
  flagEmoji: string;
  koreanPopulation?: number;
  overview?: string;
  overviewKo?: string;
  visaInfo?: string;
  costOfLiving?: Record<string, number>;
  healthcareInfo?: string;
  koreanCommunities?: string[];
  usefulLinks?: Array<{ title: string; url: string }>;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  nameKo?: string;
  avatarUrl?: string;
  avatarPreset?: string;
  bio?: string;
  location?: string;
  countryCode?: string;
  language: 'en' | 'ko';
  points: number;
  tokens: number;
  level: number;
  isVerified: boolean;
  emailVerified: boolean;
  createdAt: string;
}
